import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import IncidentAPI from './incidents';
import { CreateIncidentRequest, Incident, IncidentResponse } from '../types/incident';

export const useCreateIncident = () => {
    const queryClient = useQueryClient();

    return useMutation<IncidentResponse, Error, CreateIncidentRequest>({
        mutationFn: (data) => IncidentAPI.createIncident(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['incidents'] });
        },
    });
};

export const useGetIncidents = (page: number = 1, limit: number = 10, sortBy: string = 'created_at', order: string = 'desc', companyId?: string) => {
    return useQuery({
        queryKey: ['incidents', page, limit, sortBy, order, companyId], // Added companyId to the query key
        queryFn: () => IncidentAPI.getIncidents(page, limit, sortBy, order, companyId),
    });
};

export const useGetIncident = (id: string) => {
    return useQuery({
        queryKey: ['incident', id],
        queryFn: () => IncidentAPI.getIncident(id),
    });
};

export const useUpdateIncident = () => {
    const queryClient = useQueryClient();

    return useMutation<IncidentResponse, Error, { id: string; data: Partial<Incident> }>({
        mutationFn: ({ id, data }) => IncidentAPI.updateIncident(id, data),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['incidents'] });
            queryClient.invalidateQueries({ queryKey: ['incident', variables.id] });
        },
    });
};

export const useDeleteIncident = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: (id) => IncidentAPI.deleteIncident(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['incidents'] });
        },
    });
};

export const useGetIncidentStats = () => {
    return useQuery({
        queryKey: ['incidentStats'],
        queryFn: () => IncidentAPI.getIncidentStats(),
    });
};

export const useGetIncidentSuggestions = (id: string) => {
    return useQuery({
        queryKey: ['incidentSuggestions', id],
        queryFn: () => IncidentAPI.getIncidentSuggestions(id),
        enabled: !!id,
    });
};
